import { useEffect, useState } from 'react';

interface AppearanceSettings {
  theme: 'light' | 'dark' | 'auto';
  reduceMotion: boolean;
}

const defaultSettings: AppearanceSettings = {
  theme: 'auto',
  reduceMotion: false
};

export const useAppearance = () => {
  const [settings, setSettings] = useState<AppearanceSettings>(defaultSettings);

  // Load settings from localStorage on mount
  useEffect(() => {
    const loadSettings = () => {
      try {
        const savedSettings = localStorage.getItem('unichat-appearance-settings');
        if (savedSettings) {
          const parsed = JSON.parse(savedSettings);
          const mergedSettings = { ...defaultSettings, ...parsed };
          setSettings(mergedSettings);
          applySettings(mergedSettings);
        } else {
          // Apply default settings
          applySettings(defaultSettings);
        }
      } catch (error) {
        console.warn('Failed to load appearance settings:', error);
        applySettings(defaultSettings);
      }
    };

    loadSettings();
  }, []);

  // Apply settings to the document
  const applySettings = (newSettings: AppearanceSettings) => {
    const root = document.documentElement;
    
    // Apply theme
    if (newSettings.theme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
      root.classList.toggle('dark', prefersDark);
    } else {
      root.setAttribute('data-theme', newSettings.theme);
      root.classList.toggle('dark', newSettings.theme === 'dark');
    }
    
    // Font size removed - was not working properly
    
    // Apply reduced motion
    root.setAttribute('data-reduce-motion', newSettings.reduceMotion.toString());
  };

  // Listen for system theme changes when in auto mode
  useEffect(() => {
    if (settings.theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        applySettings(settings);
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [settings]);

  // Listen for system reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches && !settings.reduceMotion) {
        // System prefers reduced motion, but user hasn't explicitly set it
        const root = document.documentElement;
        root.setAttribute('data-reduce-motion', 'true');
      }
    };
    
    // Apply initial system preference if user hasn't set it
    if (!settings.reduceMotion && mediaQuery.matches) {
      const root = document.documentElement;
      root.setAttribute('data-reduce-motion', 'true');
    }
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [settings.reduceMotion]);

  const updateSettings = (newSettings: Partial<AppearanceSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    applySettings(updatedSettings);
    
    // Save to localStorage
    try {
      localStorage.setItem('unichat-appearance-settings', JSON.stringify(updatedSettings));
    } catch (error) {
      console.warn('Failed to save appearance settings:', error);
    }
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    applySettings(defaultSettings);
    localStorage.removeItem('unichat-appearance-settings');
  };

  return {
    settings,
    updateSettings,
    resetSettings,
    applySettings
  };
}; 